import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../utils/translations';
import i18n from '../i18n/index';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to 'en' (English)
    return localStorage.getItem('selectedLanguage') || 'en';
  });

  const [isRTL, setIsRTL] = useState(() => {
    // Initialize RTL based on the current language
    const currentLanguage = localStorage.getItem('selectedLanguage') || 'en';
    return currentLanguage === 'ar';
  });

  // Update RTL state when language changes
  useEffect(() => {
    const rtl = language === 'ar';
    setIsRTL(rtl);
    
    // Update document direction and language - default to LTR for English
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    
    // Save to localStorage
    localStorage.setItem('selectedLanguage', language);
  }, [language]);

  // Initialize document direction on first load
  useEffect(() => {
    const initialLanguage = localStorage.getItem('selectedLanguage') || 'en';
    document.documentElement.dir = initialLanguage === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = initialLanguage;
  }, []);

  const switchLanguage = (newLanguage) => {
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  const t = (key, params = {}) => {
    // Get translation for the current language
    let translation = translations[language]?.[key];
    
    // Fallback to English if translation not found
    if (!translation && language !== 'en') {
      translation = translations.en[key] || key;
    }
    
    // If no translation found, return the key
    if (!translation) {
      return key;
    }
    
    // Handle template substitution
    if (params && typeof params === 'object') {
      return translation.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
        return params[paramKey] || match;
      });
    }
    
    return translation;
  };

  const value = {
    language,
    isRTL,
    switchLanguage,
    t,
    availableLanguages: [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'ar', name: 'Arabic', nativeName: 'العربية' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;